import useStore from '../Store/market_greeks_data_store';

export const gexIndicator = function (PineJS) {
  return Promise.resolve([
    {
      name: 'Raw GEX',
      metainfo: {
        _metainfoVersion: 53,
        id: "Raw_GEX@tv-basicstudies-1",   // unchanged
        description: "Raw GEX",
        shortDescription: "Raw GEX ($ Billion)",
        format: { type: "inherit" },
        linkedToSeries: false,
        is_price_study: false,

        // Order: baseline (back), areas (low→mid→high), raw line (top)
        plots: [
          { id: "baseline4", type: "line", title: "4B Baseline" },

          { id: "low",  type: "line", title: "< 2B (area)" },
          { id: "mid",  type: "line", title: "2–4B (area)" },
          { id: "high", type: "line", title: "≥ 4B (area)" },

          { id: "gex_raw", type: "line", title: "Raw GEX (Billions)" },
        ],

        defaults: {
          styles: {
            // dashed faint baseline
            baseline4: { linestyle: 2, linewidth: 1, plottype: 0, transparency: 70, visible: true, color: "#bfbfbf", joinPoints: true },

            // AREA series to histogramBase: 4
            low:  { plottype: 8, areaStyle: 1, linewidth: 2, linestyle: 0, transparency: 70, visible: true, color: "#D2042D", joinPoints: true },
            mid:  { plottype: 8, areaStyle: 1, linewidth: 2, linestyle: 0, transparency: 70, visible: true, color: "#FFA500", joinPoints: true },
            high: { plottype: 8, areaStyle: 1, linewidth: 2, linestyle: 0, transparency: 70, visible: true, color: "#00FF00", joinPoints: true },

            // raw outline on top
            gex_raw: { plottype: 0, linewidth: 1, linestyle: 0, transparency: 0, visible: true, color: "rgba(75,142,152,0.2)", joinPoints: true },
          },
        },

        styles: {
          baseline4: { title: "4B Baseline", histogramBase: 0, joinPoints: true },

          // tell the areas to fill to 4B
          low:  { title: "< 2B",   histogramBase: 4.0, areaStyle: 1, joinPoints: true },
          mid:  { title: "2–4B",   histogramBase: 4.0, areaStyle: 1, joinPoints: true },
          high: { title: "≥ 4B",   histogramBase: 4.0, areaStyle: 1, joinPoints: true },

          gex_raw: { title: "Raw GEX (Billions)", histogramBase: 0, joinPoints: true },
        },

        inputs: [],
      },

      constructor: function () {
        const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000;

        // thresholds
        const THR2 = 2.0;
        const THR4 = 4.0;

        // seam control
        const BLEND = 0.03;   // widen if you ever see gaps (e.g. 0.05)
        const EPS   = 5e-6;   // tiny overlap; raise to 1e-5 if needed

        this._lastBand = "mid";   // "low" | "mid" | "high"
        this._lastTime = null;

        this.init = function () {
          const src = useStore.getState().spxGexData || [];
          if (!src?.length) { this._rows = []; return; }

          this._rows = src.map(entry => {
            const raw = entry.gex == null ? NaN : parseFloat(entry.gex);
            const vB  = Number.isFinite(raw) ? Math.round(raw / 1e8) / 10 : NaN; // billions, 0.1B
            const t   = Math.floor((entry.time + ONE_DAY_IN_MS) / 1000);
            return { t, vB };
          }).sort((a, b) => a.t - b.t);
        };

        this.main = function (ctx) {
          // return order: [baseline4, low, mid, high, gex_raw]
          if (!this._rows?.length) return [THR4, NaN, NaN, NaN, NaN];

          const ts = Math.floor(ctx.symbol.time / 1000);
          if (!Number.isFinite(ts)) return [THR4, NaN, NaN, NaN, NaN];

          // nearest row
          let row = null, best = Infinity;
          for (const r of this._rows) {
            const d = Math.abs(r.t - ts);
            if (d < best) { best = d; row = r; }
          }
          if (!row) return [THR4, NaN, NaN, NaN, NaN];

          const v = row.vB;

          // determine current band
          let band = "mid";
          if (Number.isFinite(v)) {
            if (v < THR2) band = "low";
            else if (v >= THR4) band = "high";
            else band = "mid";
          }

          // defaults
          let lowV = NaN, midV = NaN, highV = NaN;

          // soft blend around thresholds (both bands rendered)
          const near2 = Number.isFinite(v) && Math.abs(v - THR2) <= BLEND;
          const near4 = Number.isFinite(v) && Math.abs(v - THR4) <= BLEND;

          if (near2) {
            lowV = v - EPS;
            midV = v + EPS;
          } else if (near4) {
            midV  = v - EPS;
            highV = v + EPS;
          } else {
            // hard flip handling: if band changed since previous bar, draw both for THIS bar
            if (this._lastTime != null && band !== this._lastBand) {
              if (this._lastBand === "low" && band === "mid") {
                lowV = v - EPS; midV = v + EPS;
              } else if (this._lastBand === "mid" && band === "low") {
                lowV = v - EPS; midV = v + EPS;
              } else if (this._lastBand === "mid" && band === "high") {
                midV = v - EPS; highV = v + EPS;
              } else if (this._lastBand === "high" && band === "mid") {
                midV = v - EPS; highV = v + EPS;
              } else {
                // low <-> high leap (rare): paint current band and a tiny film of mid
                if (band === "low") { lowV = v - EPS; midV = v + EPS; }
                else { highV = v + EPS; midV = v - EPS; }
              }
            } else {
              // normal case: one active band
              if (band === "low") lowV = v;
              else if (band === "high") highV = v;
              else midV = v;
            }
          }

          this._lastBand = band;
          this._lastTime = ts;

          return [THR4, lowV, midV, highV, v]; // baseline, areas, raw line
        };
      },
    },
  ]);
};









// import useStore from '../Store/market_greeks_data_store';
//
// export const gexIndicator = function (PineJS) {
//   return Promise.resolve([
//     {
//       name: 'Raw GEX',
//       metainfo: {
//         _metainfoVersion: 53,
//         id: "Raw_GEX@tv-basicstudies-1",
//         description: "Raw GEX",
//         shortDescription: "Raw GEX ($ Billion)",
//         format: { type: "inherit"
//         },
//         linkedToSeries: false,
//         is_price_study: false,
//         plots: [
//           { id: "gex_plot", type: "line" }
//         ],
//         defaults: {
//           styles: {
//             gex_plot: {
//               linestyle: 0,
//               linewidth: 1,
//               plottype: 0,
//               trackPrice: false,
//               transparency: 20,
//               visible: true,
//               color: "#fffb00",
//             }
//           },
//         },
//         styles: {
//           gex_plot: { title: "Raw GEX (Billions)", histogramBase: 0, joinPoints: true }
//         },
//         inputs: [],
//       },
//       constructor: function () {
//         this.init = function (ctx) {
//           this._context = ctx;
//
//           const spxGexData = useStore.getState().spxGexData || [];
//           if (!spxGexData || spxGexData.length === 0) {
//             this._gexData = [];
//             return;
//           }
//
//           const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000;
//
//           this._gexData = spxGexData
//             .map(entry => {
//               const rawValue = entry.gex === null ? 0 : parseFloat(entry.gex);
//               // Round to one decimal place and convert to billions
//               const value = Math.round(rawValue / 1e8) / 10;
//
//               return {
//                 time: Math.floor((entry.time + ONE_DAY_IN_MS) / 1000),
//                 value: value,
//               };
//             });
//         };
//
//         this.main = function (ctx) {
//           const currentTime = Math.floor(ctx.symbol.time / 1000);
//
//           if (!this._gexData || this._gexData.length === 0) {
//             return [NaN];
//           }
//
//           let closestMatch = null;
//           let smallestDiff = Infinity;
//           for (const data of this._gexData) {
//             const timeDiff = Math.abs(data.time - currentTime);
//             if (timeDiff < smallestDiff) {
//               closestMatch = data;
//               smallestDiff = timeDiff;
//             }
//           }
//
//           if (closestMatch) {
//             return [closestMatch.value];
//           }
//
//           return [NaN];
//         };
//       },
//     },
//   ]);
// };